/**
 * Which page a path is, as a pure function (no router library: the app has few pages, DZGO's pattern).
 *
 * - `/` → the live hangar screen of the test site
 * - `/kiosk/<siteId>#swd_<token>`, `/kiosk/demo`, `/kiosk/demo-fallback` → the hangar screen
 * - `/manage` → sign in, then "Your sites"; `/manage/<siteId>[/<section>]` → one site; no section is the
 *   site's Overview
 * - anything else, and an unknown `/manage/<siteId>/<section>`, is `notfound`
 * - `/join#swi_<token>` → an invitation to join a site (sign in first)
 * - `/settings/brand` → the account's brand kit (`/manage/demo/settings/brand` on demo data)
 * - `/manage/demo[/…]` → the same owner screens on demo data, nothing saved
 * - `/auth/callback` → back from signing in at accounts.straticahq.com (D16)
 */

/** The product's page on the brand site. */
export const BRAND_URL = 'https://www.straticahq.com/products/weather/'

export const SITE_SECTIONS = ['overview', 'sources', 'limits', 'alerts', 'screens', 'integrations', 'members', 'brand', 'settings'] as const
export type SiteSection = (typeof SITE_SECTIONS)[number]

export type Route =
  | { page: 'front' }
  | { page: 'kiosk'; id: string; token: string }
  | { page: 'manage'; demo: boolean }
  | { page: 'site'; demo: boolean; siteId: string; section: SiteSection }
  /** The account's own brand kit (suite standard page `/settings/brand`). */
  | { page: 'brand'; demo: boolean }
  /** An invitation link (`/join#swi_…`): sign in, then join the site it names. */
  | { page: 'join'; token: string }
  /** Back from accounts.straticahq.com (D16). */
  | { page: 'callback' }
  | { page: 'notfound' }

export function parseRoute(pathname: string, hash: string): Route {
  const path = pathname.replace(/\/+$/, '')
  if (path === '') return { page: 'front' }
  if (path === '/auth/callback') return { page: 'callback' }
  const kiosk = /^\/kiosk\/([^/]+)$/.exec(path)
  if (kiosk) return { page: 'kiosk', id: decodeURIComponent(kiosk[1] ?? ''), token: hash.replace(/^#/, '') }

  if (path === '/join') return { page: 'join', token: hash.replace(/^#/, '') }

  const brand = /^(\/manage\/demo)?\/settings\/brand$/.exec(path)
  if (brand) return { page: 'brand', demo: brand[1] !== undefined }

  const m = /^\/manage(\/demo)?(?:\/([^/]+)(?:\/([^/]+))?)?$/.exec(path)
  if (!m) return { page: 'notfound' }
  const demo = m[1] !== undefined
  const siteId = m[2]
  if (siteId === undefined) return { page: 'manage', demo }
  const section = (SITE_SECTIONS as readonly string[]).includes(m[3] ?? '') ? (m[3] as SiteSection) : 'overview'
  if (m[3] !== undefined && section !== m[3]) return { page: 'notfound' }
  return { page: 'site', demo, siteId: decodeURIComponent(siteId), section }
}

/** The account's brand kit page. */
export function brandPath(demo: boolean): string {
  return demo ? '/manage/demo/settings/brand' : '/settings/brand'
}

/** The path for an owner page; `demo` keeps a demo visitor inside the demo. */
export function managePath(demo: boolean, siteId?: string, section?: SiteSection): string {
  const base = demo ? '/manage/demo' : '/manage'
  if (!siteId) return base
  const site = `${base}/${encodeURIComponent(siteId)}`
  return section && section !== 'overview' ? `${site}/${section}` : site
}
